import { useTranslation } from 'react-i18next';
import { Check } from 'lucide-react';
import { platformIcon } from './platformVisuals';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { effectiveAnalyticsSyncStatus, isNonActiveAnalyticsConnection } from '@/lib/social-analytics/connectionState';
import { cn } from '@/lib/utils';

export type AnalyticsConnection = {
  id: string;
  platform: string;
  username: string;
  display_name?: string | null;
  status?: string | null;
  analytics_sync_status?: string | null;
  analytics_last_success_at?: string | null;
};

type AccountFiltersProps = {
  connections: readonly AnalyticsConnection[];
  selectedConnectionIds: readonly string[];
  onSelectionChange: (connectionIds: string[]) => void;
};

export const AccountFilters = ({
  connections,
  selectedConnectionIds,
  onSelectionChange,
}: AccountFiltersProps) => {
  const { t } = useTranslation();
  const allSelected = selectedConnectionIds.length === 0;

  const toggleConnection = (id: string) => {
    if (allSelected) {
      onSelectionChange([id]);
      return;
    }

    const next = selectedConnectionIds.includes(id)
      ? selectedConnectionIds.filter((connectionId) => connectionId !== id)
      : [...selectedConnectionIds, id];
    onSelectionChange(next);
  };

  return (
    <section aria-labelledby="analytics-account-filter-label" className="space-y-2">
      <div className="flex items-center justify-between gap-4">
        <p id="analytics-account-filter-label" className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
          {t('analytics.filters.accounts')}
        </p>
        <p className="text-xs text-muted-foreground">{t('analytics.filters.multiSelectHint')}</p>
      </div>
      <TooltipProvider delayDuration={120}>
        <div className="-mx-1 flex gap-2 overflow-x-auto px-1 pb-1" role="group" aria-labelledby="analytics-account-filter-label">
          <Button
            type="button"
            variant={allSelected ? 'default' : 'outline'}
            size="sm"
            aria-pressed={allSelected}
            onClick={() => onSelectionChange([])}
            className="shrink-0"
          >
            {allSelected && <Check className="h-3.5 w-3.5" />}
            {t('analytics.filters.allAccounts')}
          </Button>
          {connections.map((connection) => {
            const selected = !allSelected && selectedConnectionIds.includes(connection.id);
            const Icon = platformIcon(connection.platform);
            const reconnectRequired = effectiveAnalyticsSyncStatus(connection) === 'reconnect_required';
            const nonActive = isNonActiveAnalyticsConnection(connection);
            const accountName = `@${connection.username}`;
            return (
              <Tooltip key={connection.id}>
                <TooltipTrigger asChild>
                  <Button
                    type="button"
                    variant={selected ? 'secondary' : 'outline'}
                    size="sm"
                    aria-pressed={selected}
                    onClick={() => toggleConnection(connection.id)}
                    className={cn(
                      'shrink-0 border-border/80',
                      selected && 'border-primary/30 bg-primary/10 text-foreground',
                      reconnectRequired && 'border-amber-500/40',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span>{accountName}</span>
                    {reconnectRequired && (
                      <Badge variant="outline" className="border-amber-500/40 px-1 py-0 text-[10px] text-amber-700 dark:text-amber-300">
                        {t(nonActive ? 'analytics.status.nonActiveShort' : 'analytics.status.reconnectRequiredShort')}
                      </Badge>
                    )}
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  {connection.display_name || accountName} · {t(`analytics.platforms.${connection.platform}`)}
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    </section>
  );
};
